import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, RadialBarChart, RadialBar } from 'recharts';
import { useExecutiveKPIs } from '../../hooks/useExecutiveKPIs';
import LoadingSpinner from '../common/LoadingSpinner';
import StatsCard from '../common/StatsCard';
import { ExclamationTriangleIcon, CheckCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { formatCurrency } from '../../utils/formatters';

interface Props {
  dateRange: { start_date?: string; end_date?: string };
}

export default function ExecutiveReportTab({ dateRange }: Props) {
  const { data: kpis, isLoading, error } = useExecutiveKPIs(dateRange);

  if (isLoading) return <LoadingSpinner />;
  if (error) return <div className="text-red-500">Erreur lors du chargement des données</div>;
  if (!kpis) return null;

  // Données pour le graphique radial des objectifs
  const objectivesData = [
    { name: 'Taux de Paiement', value: kpis.monthly_summary.payment_rate, fill: '#8884d8' },
    { name: 'Croissance', value: Math.max(0, kpis.monthly_summary.growth_rate), fill: '#82ca9d' },
    { name: 'Marge', value: kpis.financial_overview.profit_margin, fill: '#ffc658' }
  ];

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'error': return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      case 'warning': return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'info': return <InformationCircleIcon className="h-5 w-5 text-blue-500" />;
      default: return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Résumé Exécutif */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-6 rounded-lg">
        <h2 className="text-2xl font-bold mb-4">🎯 Résumé Exécutif</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white bg-opacity-20 p-4 rounded">
            <div className="text-3xl font-bold">{kpis.monthly_summary.patients_this_month}</div>
            <div className="text-sm opacity-80">Patients ce mois</div>
          </div>
          <div className="bg-white bg-opacity-20 p-4 rounded">
            <div className="text-3xl font-bold">{formatCurrency(kpis.monthly_summary.revenue_this_month)}</div>
            <div className="text-sm opacity-80">CA du mois</div>
          </div>
          <div className="bg-white bg-opacity-20 p-4 rounded">
            <div className="text-3xl font-bold">{kpis.monthly_summary.payment_rate.toFixed(1)}%</div>
            <div className="text-sm opacity-80">Taux de paiement</div>
          </div>
          <div className="bg-white bg-opacity-20 p-4 rounded">
            <div className="text-3xl font-bold">{kpis.monthly_summary.appointments_today}</div>
            <div className="text-sm opacity-80">RDV aujourd'hui</div>
          </div>
        </div>
      </div>

      {/* KPIs Financiers */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard
          title="CA Annuel"
          value={formatCurrency(kpis.financial_overview.total_revenue_ytd)}
          icon="💰"
          color="green"
          change={{ 
            value: kpis.monthly_summary.growth_rate, 
            type: kpis.monthly_summary.growth_rate >= 0 ? 'increase' : 'decrease' 
          }}
        />
        <StatsCard
          title="Créances Totales"
          value={formatCurrency(kpis.financial_overview.total_outstanding)}
          icon="📋"
          color="red"
        />
        <StatsCard
          title="Délai Moyen"
          value={`${kpis.financial_overview.avg_collection_time.toFixed(0)} jours`}
          icon="⏰"
          color="yellow"
        />
        <StatsCard
          title="Marge Bénéficiaire"
          value={`${kpis.financial_overview.profit_margin.toFixed(1)}%`}
          icon="📈"
          color="purple"
        />
      </div>

      {/* Alertes et Notifications */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">🚨 Alertes et Points d'Attention</h3>
        <div className="space-y-3">
          {kpis.alerts.length > 0 ? (
            kpis.alerts.map((alert, index: number) => (
              <div key={index} className={`flex items-center space-x-3 p-3 rounded-lg ${
                alert.type === 'error' ? 'bg-red-50' :
                alert.type === 'warning' ? 'bg-yellow-50' :
                'bg-blue-50'
              }`}>
                {getAlertIcon(alert.type)}
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{alert.message}</p>
                  <p className="text-xs text-gray-500">
                    Valeur actuelle: {alert.value} | Seuil: {alert.threshold}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
              <CheckCircleIcon className="h-5 w-5 text-green-500" />
              <p className="text-sm text-green-800">Aucune alerte critique - Tout fonctionne normalement</p>
            </div>
          )}
        </div>
      </div>

      {/* Top Performers et Objectifs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performers */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">🏆 Top Performers</h3>
          <div className="space-y-4">
            <div className="border-l-4 border-blue-500 pl-4">
              <h4 className="font-medium text-gray-900">Meilleur Médecin</h4>
              <p className="text-lg font-bold text-blue-600">{kpis.top_performers.best_doctor.name}</p>
              <p className="text-sm text-gray-500">{formatCurrency(kpis.top_performers.best_doctor.revenue)} de revenus</p>
            </div>
            <div className="border-l-4 border-green-500 pl-4">
              <h4 className="font-medium text-gray-900">Traitement le Plus Demandé</h4>
              <p className="text-lg font-bold text-green-600">{kpis.top_performers.best_treatment.name}</p>
              <p className="text-sm text-gray-500">{kpis.top_performers.best_treatment.count} traitements</p>
            </div>
            <div className="border-l-4 border-purple-500 pl-4">
              <h4 className="font-medium text-gray-900">Meilleure Assurance</h4>
              <p className="text-lg font-bold text-purple-600">{kpis.top_performers.best_insurance.name}</p>
              <p className="text-sm text-gray-500">{kpis.top_performers.best_insurance.coverage}% de couverture</p>
            </div>
          </div>
        </div>

        {/* Objectifs et Performance */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">📊 Performance vs Objectifs</h3>
          <RadialBarChart width={400} height={300} data={objectivesData}>
            <RadialBar
              label={{ position: 'insideStart', fill: '#fff' }}
              background
              dataKey="value"
            />
            <Legend iconSize={10} layout="horizontal" verticalAlign="bottom" />
            <Tooltip formatter={(value) => [`${value}%`, 'Performance']} />
          </RadialBarChart>
        </div>
      </div>

      {/* Actions Recommandées */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">💡 Actions Recommandées</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="p-4 border border-gray-200 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">📈 Optimisation Revenus</h4>
            <p className="text-sm text-gray-600">
              Augmenter la fréquence des traitements les plus rentables et revoir la tarification.
            </p>
          </div>
          <div className="p-4 border border-gray-200 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">⏱️ Gestion Créances</h4>
            <p className="text-sm text-gray-600">
              Mettre en place un système de relance automatique pour réduire les délais de paiement.
            </p>
          </div>
          <div className="p-4 border border-gray-200 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">👥 Fidélisation Patients</h4>
            <p className="text-sm text-gray-600">
              Développer un programme de suivi pour augmenter le taux de rétention des patients.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}